import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { KeyRound, Mail, MailPlus, Pencil, Plus, ShieldOff, Unlock, UserCheck, UserX } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useMe } from '../../api/auth';
import { ApiError } from '../../api/client';
import { useDeactivateUser, useOrganisationList, useResetTwoFactor, useSendPasswordReset, useSendWelcome, useTemporaryPassword, useUnlockUser, useUpdateUser, useUsers, type User } from '../../api/users';
import { usePermission } from '../../auth/permissions';
import { DataTable, type Column } from '../../components/DataTable';
import { useDialog } from '../../components/dialogs/DialogProvider';
import { EmptyState } from '../../components/EmptyState';
import { RowMenu } from '../../components/RowMenu';
import { StatusLabel } from '../../components/StatusLabel';
import { Tag } from '../../components/Tag';
import { useToast } from '../../components/Toast';
import { formatAgo, formatDateTime } from '../../lib/format';
import { ROLE_INFO } from './roles';
import { TemporaryPasswordDialog } from './TemporaryPasswordDialog';
import { UserDialog } from './UserDialog';

const isLocked = (u: User) => !!u.lockedUntil && new Date(u.lockedUntil) > new Date();

function status(u: User) {
  if (!u.isActive) return <StatusLabel tone="off">Deactivated</StatusLabel>;
  if (isLocked(u)) return <StatusLabel tone="fault">Locked</StatusLabel>;
  if (u.mustChangePassword) return <StatusLabel tone="neutral">Temporary password</StatusLabel>;
  if (!u.lastLoginAt) return <StatusLabel tone="neutral">Not signed in yet</StatusLabel>;
  if (!u.mfaRequired) return <StatusLabel tone="neutral">Two-factor off</StatusLabel>;
  if (!u.mfaEnabledAt) return <StatusLabel tone="neutral">Two-factor at next sign-in</StatusLabel>;
  return <StatusLabel tone="success">Active</StatusLabel>;
}

/** Settings, Users: who can sign in, their role and organisations. */
export function UsersTab() {
  const users = useUsers();
  const orgs = useOrganisationList();
  const { data: me } = useMe();
  const canEdit = usePermission('users.edit');
  const dialog = useDialog();
  const toast = useToast();
  const [showInactive, setShowInactive] = useState(false);

  const deactivate = useDeactivateUser();
  const update = useUpdateUser();
  const unlock = useUnlockUser();
  const resetTwoFactor = useResetTwoFactor();
  const sendReset = useSendPasswordReset();
  const sendWelcome = useSendWelcome();
  const tempPassword = useTemporaryPassword();

  const orgName = useMemo(() => new Map((orgs.data ?? []).map((o) => [o.id, o.businessName])), [orgs.data]);
  const orgSummary = (u: User) => {
    if (u.role === 'admin') return 'All organisations';
    const names = u.organisationIds.map((id) => orgName.get(id) ?? 'Another organisation');
    if (names.length === 0) return 'None';
    return names.length <= 2 ? names.join(', ') : `${names.slice(0, 2).join(', ')} and ${names.length - 2} more`;
  };

  const rows = (users.data ?? []).filter((u) => showInactive || u.isActive);
  const inactiveCount = (users.data ?? []).filter((u) => !u.isActive).length;

  /** Runs an action and reports how it went. */
  const run = async (fn: () => Promise<unknown>, done: string) => {
    try {
      await fn();
      toast(done);
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Something went wrong. Please try again.', 'error');
    }
  };

  const columns: Column<User>[] = [
    {
      key: 'name',
      header: 'Name',
      sortValue: (u) => u.displayName.toLowerCase(),
      render: (u) => (
        <Box>
          <Typography sx={{ fontWeight: 500 }}>
            {u.displayName}
            {u.id === me?.id && <Typography component="span" color="textSecondary"> (you)</Typography>}
          </Typography>
          <Typography variant="body2" color="textSecondary">{u.email}</Typography>
        </Box>
      ),
    },
    { key: 'role', header: 'Role', sortValue: (u) => ['admin', 'manager', 'user'].indexOf(u.role), render: (u) => <Tag strong={u.role === 'admin'} mint={u.role === 'manager'}>{ROLE_INFO[u.role].label}</Tag> },
    { key: 'orgs', header: 'Organisations', hideBelow: 'md', render: (u) => <Typography color={u.role === 'admin' ? 'textSecondary' : undefined}>{orgSummary(u)}</Typography> },
    {
      key: 'last',
      header: 'Last signed in',
      hideBelow: 'sm',
      sortValue: (u) => (u.lastLoginAt ? -new Date(u.lastLoginAt).getTime() : null),
      render: (u) => <span title={formatDateTime(u.lastLoginAt)}>{formatAgo(u.lastLoginAt) || '—'}</span>,
    },
    { key: 'status', header: 'Status', render: status },
  ];

  const actions = (u: User) => {
    const self = u.id === me?.id;
    return (
      <RowMenu
        label={`Actions for ${u.displayName}`}
        items={[
          { label: 'Edit', icon: Pencil, onClick: () => void dialog.open(UserDialog, { user: u }) },
          {
            label: 'Unlock',
            icon: Unlock,
            hidden: !isLocked(u),
            onClick: () => void run(() => unlock.mutateAsync(u.id), `${u.displayName} can sign in again.`),
          },
          {
            label: 'Reset two-factor sign-in',
            icon: ShieldOff,
            hidden: !u.isActive || !u.mfaEnabledAt || !canEdit,
            onClick: async () => {
              const ok = await dialog.confirm({
                title: `Reset ${u.displayName}'s two-factor sign-in?`,
                message: 'For a lost or new phone. They are signed out everywhere and set up their authenticator app again at their next sign-in.',
                confirmLabel: 'Reset two-factor',
              });
              if (ok) await run(() => resetTwoFactor.mutateAsync(u.id), `${u.displayName} sets up their authenticator app at their next sign-in.`);
            },
          },
          {
            label: 'Send the welcome email again',
            icon: MailPlus,
            hidden: !u.isActive || self || !canEdit,
            onClick: () => void run(() => sendWelcome.mutateAsync(u.id), `Welcome email sent to ${u.email}.`),
          },
          {
            label: 'Email a password reset link',
            icon: Mail,
            hidden: !u.isActive || self,
            onClick: () => void run(() => sendReset.mutateAsync(u.id), `Reset link sent to ${u.email}.`),
          },
          {
            label: 'Set a temporary password',
            icon: KeyRound,
            hidden: !u.isActive || self,
            onClick: async () => {
              const ok = await dialog.confirm({
                title: `Set a temporary password for ${u.displayName}?`,
                message: 'Their current password stops working and they are signed out everywhere. You give them the new one; they choose their own when they sign in.',
                confirmLabel: 'Set a temporary password',
              });
              if (!ok) return;
              try {
                const r = await tempPassword.mutateAsync(u.id);
                await dialog.open(TemporaryPasswordDialog, { name: u.displayName, password: r.temporaryPassword, another: async () => (await tempPassword.mutateAsync(u.id)).temporaryPassword });
              } catch (e) {
                toast(e instanceof ApiError ? e.message : 'Something went wrong.', 'error');
              }
            },
          },
          {
            label: 'Reactivate',
            icon: UserCheck,
            hidden: u.isActive,
            onClick: () => void run(() => update.mutateAsync({ id: u.id, isActive: true }), `${u.displayName} can sign in again.`),
          },
          {
            label: 'Deactivate',
            icon: UserX,
            danger: true,
            hidden: !u.isActive || self,
            onClick: async () => {
              const ok = await dialog.confirm({
                title: `Deactivate ${u.displayName}?`,
                message: 'They are signed out at once and can no longer sign in. Their history stays, and you can reactivate them later.',
                confirmLabel: 'Deactivate',
                danger: true,
              });
              if (ok) await run(() => deactivate.mutateAsync(u.id), `${u.displayName} was deactivated.`);
            },
          },
        ]}
      />
    );
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ justifyContent: 'space-between', alignItems: { sm: 'flex-end' }, mb: 3 }}>
        <Box>
          <Typography variant="h2">Users.</Typography>
          <Typography color="textSecondary" sx={{ mt: 0.5 }}>
            {canEdit ? 'Who can sign in to Pulsepoint, and what they can see.' : 'The people in your organisations who can sign in.'}
          </Typography>
        </Box>
        {canEdit && (
          <Button variant="contained" startIcon={<Plus size={20} strokeWidth={1.75} />} onClick={() => void dialog.open(UserDialog, {})}>
            Add a person
          </Button>
        )}
      </Stack>
      <DataTable
        label="Users"
        rows={rows}
        columns={columns}
        rowKey={(u) => u.id}
        initialSort={{ key: 'name', direction: 'asc' }}
        searchText={(u) => `${u.displayName} ${u.email} ${u.username ?? ''} ${orgSummary(u)}`}
        searchPlaceholder="Search by name, email or organisation"
        toolbar={
          inactiveCount > 0 && (
            <FormControlLabel
              control={<Switch checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />}
              label={`Show deactivated (${inactiveCount})`}
            />
          )
        }
        onRowClick={canEdit ? (u) => void dialog.open(UserDialog, { user: u }) : undefined}
        rowActions={canEdit ? actions : undefined}
        loading={users.isPending}
        error={users.error}
        onRetry={() => void users.refetch()}
        empty={<EmptyState title="Nobody here yet">Users you add can sign in and see the organisations you choose.</EmptyState>}
      />
    </Box>
  );
}
